import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API } from "../lib/api";
import CircuitTable from "../components/CircuitTable";
import RechargeModal from "../components/RechargeModal";
import CancelModal from "../components/CancelModal";
import { useAuth } from "../context/AuthContext";
import { fmtINR, fmtDate, daysLabel } from "../lib/format";
import { toast } from "sonner";
import { Download, Users2 } from "lucide-react";

export default function ActionRequired() {
  const { can } = useAuth();
  const [sp] = useSearchParams();
  const verifyView = sp.get("view") === "verify";
  const [filters, setFilters] = useState({ q: "", service_type: "", billing_type: "", status: "Active", vendor_id: "", bucket: "" });
  const [vendors, setVendors] = useState([]);
  const [users, setUsers] = useState([]);
  const [recharge, setRecharge] = useState(null);
  const [cancel, setCancel] = useState(null);
  const [selected, setSelected] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [verifyList, setVerifyList] = useState([]);

  useEffect(() => {
    api.get("/vendors").then((r) => setVendors(r.data));
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const loadVerify = useCallback(() => {
    if (verifyView) api.get("/dashboard/verification-pending").then((r) => setVerifyList(r.data));
  }, [verifyView]);
  useEffect(() => { loadVerify(); }, [loadVerify, refresh]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const done = () => setRefresh((r) => r + 1);

  const doVerify = async (c) => {
    try { await api.post(`/circuits/${c.id}/verify`, {}); toast.success("Recharge verified — cycle completed"); done(); }
    catch (e) { toast.error("Verify failed"); }
  };

  const exportSel = async () => {
    const token = localStorage.getItem("cp_token");
    const ids = selected.length ? `?ids=${selected.join(",")}` : "";
    try {
      const r = await fetch(`${API}/export/circuits${ids}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { toast.error(r.status === 403 ? "You don't have export permission." : "Export failed."); return; }
      const b = await r.blob();
      const url = URL.createObjectURL(b); const a = document.createElement("a");
      a.href = url; a.download = "circuits.csv"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed."); }
  };

  const bulkAssign = async (uid) => {
    if (!uid || selected.length === 0) return;
    await api.post("/bulk/circuits", { circuit_ids: selected, action: "assign", assigned_accounts_id: uid });
    toast.success(`Assigned ${selected.length} circuit(s)`); setSelected([]); done();
  };

  const field = "h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-400";

  if (verifyView) {
    return (
      <div className="space-y-5">
        <h1 className="font-head text-2xl font-extrabold">Recharge Verification Pending</h1>
        <p className="text-slate-500 text-sm">Confirm internet/payment is working after recharge to complete the renewal cycle.</p>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
              <th className="px-4 py-3">Customer</th><th className="px-4 py-3">Vendor</th><th className="px-4 py-3">Billable Item</th>
              <th className="px-4 py-3">Recharged On</th><th className="px-4 py-3">New Expiry</th><th className="px-4 py-3 text-right">Action</th></tr></thead>
            <tbody>
              {verifyList.map((c) => (
                <tr key={c.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 font-semibold">{c.customer_name}</td>
                  <td className="px-4 py-3">{c.vendor_name}</td>
                  <td className="px-4 py-3 font-mono-num text-xs">{c.internal_circuit_id}</td>
                  <td className="px-4 py-3 font-mono-num text-xs">{fmtDate(c.last_recharge_date)}</td>
                  <td className="px-4 py-3 font-mono-num text-xs">{fmtDate(c.current_expiry_date)}</td>
                  <td className="px-4 py-3 text-right">
                    {can("verify") ? <button onClick={() => doVerify(c)} data-testid={`verify-btn-${c.id}`} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">Mark Verified</button> : <span className="text-xs text-slate-400">No permission</span>}
                  </td>
                </tr>
              ))}
              {verifyList.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Nothing pending verification.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-head text-2xl font-extrabold">Action Required</h1>
        <div className="flex items-center gap-2">
          {selected.length > 0 && can("edit") && (
            <select onChange={(e) => bulkAssign(e.target.value)} className={field} data-testid="bulk-assign">
              <option value="">Bulk assign to…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          {can("export") && <button onClick={exportSel} data-testid="export-btn" className="h-9 px-3 rounded-lg border border-slate-200 text-sm flex items-center gap-2 hover:bg-slate-50"><Download size={15} /> Export {selected.length ? `(${selected.length})` : "all"}</button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <input value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Search customer / circuit / ISP…" className={`${field} min-w-[220px] flex-1`} data-testid="filter-search" />
        <select value={filters.service_type} onChange={(e) => set("service_type", e.target.value)} className={field} data-testid="filter-service"><option value="">All Services</option><option>Broadband</option><option>ILL</option><option>Other</option></select>
        <select value={filters.billing_type} onChange={(e) => set("billing_type", e.target.value)} className={field} data-testid="filter-billing"><option value="">Prepaid + Postpaid</option><option>Prepaid</option><option>Postpaid</option></select>
        <select value={filters.vendor_id} onChange={(e) => set("vendor_id", e.target.value)} className={field} data-testid="filter-vendor"><option value="">All ISP/BSO</option>{vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select>
        <select value={filters.bucket} onChange={(e) => set("bucket", e.target.value)} className={field} data-testid="filter-bucket">
          <option value="">All buckets</option><option value="overdue">Overdue</option><option value="today">Today</option><option value="d1_3">1-3 days</option><option value="d4_7">4-7 days</option><option value="d8_15">8-15 days</option><option value="d16_30">16-30 days</option>
        </select>
        <select value={filters.status} onChange={(e) => set("status", e.target.value)} className={field} data-testid="filter-status">
          <option value="">All statuses</option><option>Active</option><option>Vendor Disconnection Pending</option><option>Disconnected</option><option>Temporarily Suspended</option>
        </select>
      </div>

      <CircuitTable baseFilters={filters} onRecharge={setRecharge} onCancel={setCancel} selectable selected={selected} setSelected={setSelected} refreshKey={refresh} />

      {recharge && <RechargeModal circuit={recharge} onClose={() => setRecharge(null)} onDone={done} />}
      {cancel && <CancelModal circuit={cancel} onClose={() => setCancel(null)} onDone={done} />}
    </div>
  );
}
